/**
 * <s-decal-swap> lets a shopper switch the one-of-a-kind decal on a tumbler already in the cart.
 *
 * Commands (buttons with commandfor pointing at this element):
 *   --choose-decal — reads [data-decal-variant] and [data-decal-title] from the source button.
 *
 * Element dataset:
 *   data-line-key       — cart line key of the tumbler.
 *   data-quantity       — tumbler line quantity (kept unchanged).
 *   data-property       — line property name that stores the decal ("Decal").
 *   data-old-decal-key  — cart line key of the decal currently in the cart (may be empty).
 *   data-error-message  — fallback error text.
 * Child markup:
 *   script[type="application/json"][data-properties] — the tumbler's current line properties.
 *   [data-decal-error] — inline error region.
 *
 * Flow: add the new decal first (fails cleanly if someone just bought it), then update the tumbler's
 * decal property and remove the old decal line so its stock is released.
 */
import { getCartErrorMessage } from '@theme/cart-feedback';

class SDecalSwap extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command !== '--choose-decal') return;
      const source = event.source;
      if (!(source instanceof HTMLElement)) return;
      this.#swap(source.dataset.decalVariant, source.dataset.decalTitle);
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #properties() {
    const script = this.querySelector('script[data-properties]');
    try {
      return JSON.parse(script?.textContent || '{}') || {};
    } catch {
      return {};
    }
  }

  async #swap(variantId, title) {
    if (!variantId || !title || !window.Shopify?.actions?.updateCart) return;
    if (this.getAttribute('aria-busy') === 'true') return;

    this.setAttribute('aria-busy', 'true');
    this.#showError('');

    try {
      const added = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-decal-swap' } } }
      );
      const addError = added?.userErrors?.[0]?.message;
      if (addError) {
        this.#showError(addError);
        return;
      }

      const properties = { ...this.#properties(), [this.dataset.property]: title };
      const attributes = Object.entries(properties)
        .filter(([, value]) => String(value ?? '').trim() !== '')
        .map(([key, value]) => ({ key, value: String(value) }));

      const lines = [
        { id: this.dataset.lineKey, quantity: Number.parseInt(this.dataset.quantity || '1', 10) || 1, attributes },
      ];
      if (this.dataset.oldDecalKey) lines.push({ id: this.dataset.oldDecalKey, quantity: 0 });

      const updated = await window.Shopify.actions.updateCart(
        { lines },
        { event: { context: 'cart', detail: { source: 's-decal-swap' } } }
      );
      const updateError = updated?.userErrors?.[0]?.message;
      if (updateError) this.#showError(updateError);
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-decal-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-decal-swap')) {
  customElements.define('s-decal-swap', SDecalSwap);
}
